import {
  assetUrl,
  projectUrl,
  readJson,
  resolvePresentAddons,
  STANDOUT_ANIMATION,
  type SourceZone,
} from "@libertify/experience-viewer";
import {
  STANDOUT_MARKERS,
  standoutMarkerFor,
  standoutMarkerFrom,
  type StandoutMarker,
  type StandoutNotation,
} from "@libertify/ui";
import { API_URL } from "@/constants";
import {
  MAX_SELECTION_TITLE_LENGTH,
  PADDING_PRESETS,
  STYLE_COLORS,
} from "./constants";
import type {
  MediaAsset,
  MediaProjectResponse,
  PresetColor,
  RoughNotation,
  Standout,
  StandoutRow,
  StandoutsContent,
  StandoutsDocument,
  StandoutStyle,
  StrokedNotation,
} from "./types";

/** One key for the standouts read and the cache the save writes. */
export const standoutsKey = (projectId: string) =>
  ["standouts", API_URL, projectId] as const;

/**
 * The Standouts read chain, mirroring `loadQuiz`:
 *
 *   project → storyboards[].add_ons[] → the Standouts asset_id
 *   asset   → signed path
 *   path    → `Standouts { standouts: [{ title, content, sources }] }`
 *
 * The project's documents come along from the first read, for the PDF pane.
 *
 * Strict where the viewer is lenient: a document that can't be read rejects
 * instead of reading as empty, so the editor never saves an empty list over
 * the real one.
 */
export const loadStandouts = async (
  projectId: string,
  token: string | null | undefined
): Promise<StandoutsContent> => {
  const project = await readJson<MediaProjectResponse>(
    projectUrl(API_URL, projectId),
    token
  );
  const assetId = resolvePresentAddons(project).get("Standouts")?.asset_id;
  if (!assetId) return { document: null };
  const asset = await readJson<MediaAsset>(assetUrl(API_URL, assetId), token);
  const document = await readJson<StandoutsDocument>(asset.path);
  return {
    document,
    assetId,
    description: asset.description,
    projectDocuments: project.documents,
  };
};

/** A marker as the `rough_notation` the BE stores. */
const toRoughNotation = (marker: StandoutMarker): RoughNotation => ({
  animate: STANDOUT_ANIMATION.animate,
  type: marker.notation,
  animation_delay: marker.animationDelay ?? STANDOUT_ANIMATION.animationDelay,
  animation_duration:
    marker.animationDuration ?? STANDOUT_ANIMATION.animationDuration,
  stroke_width: marker.strokeWidth,
  padding: marker.padding,
  color: marker.color,
  brackets: marker.brackets ?? [],
  iterations: marker.iterations,
  multiline: marker.multiline,
  show: true,
});

/**
 * Switch a mark's type, keeping every other field. A highlight stores no
 * stroke, and a stroked mark at 0 draws nothing, so those two come from the
 * palette when the new type needs them.
 */
const withNotation = (
  roughNotation: RoughNotation,
  notation: StandoutNotation
): RoughNotation => {
  if (roughNotation.type === notation) return roughNotation;
  const palette = STANDOUT_MARKERS.find((m) => m.notation === notation);
  return {
    ...roughNotation,
    type: notation,
    stroke_width: roughNotation.stroke_width || (palette?.strokeWidth ?? 0),
    iterations: roughNotation.iterations || (palette?.iterations ?? 0),
  };
};

/** A mark with the popover's picks over it; an unset field keeps the mark's. */
const withStyle = (
  roughNotation: RoughNotation,
  style: StandoutStyle | undefined
): RoughNotation => ({
  ...roughNotation,
  color: style?.color ?? roughNotation.color,
  stroke_width: style?.stroke_width ?? roughNotation.stroke_width,
  padding: style?.padding ?? roughNotation.padding,
});

const hasStyle = (style: StandoutStyle | undefined): boolean =>
  style?.color !== undefined ||
  style?.stroke_width !== undefined ||
  style?.padding !== undefined;

/** The style popover's preset matching `color`, if any. */
export const findPresetColor = (color: string): PresetColor | undefined =>
  STYLE_COLORS.find((c) => c.value.toLowerCase() === color.toLowerCase());

/**
 * What Default draws: the loaded color, unless that is a preset saved earlier.
 * The original is gone then, so it is the type's palette color.
 */
export const defaultColor = (row: StandoutRow): string => {
  const loaded = row.roughNotation.color;
  if (!findPresetColor(loaded)) return loaded;
  return (
    STANDOUT_MARKERS.find((m) => m.notation === row.notation)?.color ?? loaded
  );
};

export const isStrokedNotation = (
  notation: StandoutNotation
): notation is StrokedNotation => Object.hasOwn(PADDING_PRESETS, notation);

/**
 * Switch a row's type. Padding resets to the new type's palette padding (its
 * Normal preset) rather than carrying, say, a loose circle onto a highlight.
 * Back on the loaded type it keeps the loaded padding, which may be no preset.
 */
export const changeNotation = (
  row: StandoutRow,
  notation: StandoutNotation
): StandoutRow => ({
  ...row,
  notation,
  style: {
    ...row.style,
    padding:
      notation === row.roughNotation.type
        ? undefined
        : STANDOUT_MARKERS.find((m) => m.notation === notation)?.padding,
  },
});

/** Session-only row ids: unique for the life of the page is all they need. */
let nextRowId = 0;
const newRowId = (): string => `standout-row-${++nextRowId}`;

/**
 * The stored standouts as editable rows. A standout with no saved mark gets
 * the one the viewer shows for it, derived from its index — the same id
 * `parseStandouts` gives it.
 */
export const toRows = (standouts: Standout[]): StandoutRow[] =>
  standouts.map((standout, index) => {
    const saved = standout.sources?.[0]?.rough_notation;
    const marker = standoutMarkerFrom(`standout-${index}`, saved);
    return {
      id: newRowId(),
      title: standout.title,
      notation: marker.notation,
      roughNotation:
        saved?.type === marker.notation ? saved : toRoughNotation(marker),
      standout,
    };
  });

/**
 * A selected quote as a title: one line (the quote keeps the PDF's line
 * breaks), cut at a word before the limit.
 */
const toSelectionTitle = (quote: string): string => {
  const line = quote.replace(/\s+/g, " ");
  if (line.length <= MAX_SELECTION_TITLE_LENGTH) return line;
  const cut = line.slice(0, MAX_SELECTION_TITLE_LENGTH);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

/**
 * A standout made from a text selection: the quote as its content, and a
 * one-line cut of it as its title. The mark is the palette's for that type;
 * the viewer tints a highlight when it draws it.
 */
export const newStandoutRow = (
  zone: SourceZone,
  documentId: string,
  notation: StandoutNotation
): StandoutRow => {
  const id = newRowId();
  const palette = STANDOUT_MARKERS.find((m) => m.notation === notation);
  const roughNotation = toRoughNotation({
    ...(palette ?? standoutMarkerFor(id)),
    notation,
  });
  const content = zone.quote.trim();
  const title = toSelectionTitle(content);
  return {
    id,
    title,
    notation,
    roughNotation,
    standout: {
      title,
      content,
      sources: [
        { document_id: documentId, zone, rough_notation: roughNotation },
      ],
    },
  };
};

/**
 * A row moved to a new passage: its first source points at the selection, and
 * the quote becomes its content. The title and the mark's style stay; the
 * picked type is applied when it saves.
 */
export const withPassage = (
  row: StandoutRow,
  zone: SourceZone,
  documentId: string,
  notation: StandoutNotation
): StandoutRow => {
  const [first, ...rest] = row.standout.sources ?? [];
  return {
    ...row,
    notation,
    standout: {
      ...row.standout,
      content: zone.quote.trim(),
      sources: [{ ...first, document_id: documentId, zone }, ...rest],
    },
  };
};

/** A row the backend never held, as built from a selection; null if partial. */
const unsavedRow = (row: Partial<StandoutRow>): StandoutRow | null => {
  const { id, notation, roughNotation, standout } = row;
  if (!id || !notation || !roughNotation || !standout) return null;
  return { id, notation, roughNotation, standout, title: standout.title };
};

/**
 * What a row saves as. A cleared title keeps the saved one, or for a new row
 * the one it was created with.
 */
export const resolveRow = (
  row: Partial<StandoutRow>,
  saved: StandoutRow | undefined
): StandoutRow | null => {
  const base = saved ?? unsavedRow(row);
  if (!base) return null;
  const title = row.title?.trim() ? row.title : base.title;
  return { ...base, ...row, title };
};

/**
 * The rows as the asset stores them. Every source gets a `rough_notation`:
 * without one the viewer derives the mark from the index, which shifts after
 * a delete. A source's own mark is spread so fields the card doesn't edit
 * survive, and its type and style only change when the row's were edited.
 */
export const toWire = (rows: StandoutRow[]): Standout[] =>
  rows.map(({ title, notation, roughNotation, style, standout }) => {
    const edited = notation !== roughNotation.type || hasStyle(style);
    return {
      ...standout,
      title,
      sources: standout.sources?.map((source) => {
        const saved = source.rough_notation;
        if (!saved) {
          return {
            ...source,
            rough_notation: withStyle(
              withNotation(roughNotation, notation),
              style
            ),
          };
        }
        const roughNotationToSave = edited
          ? withStyle(withNotation(saved, notation), style)
          : saved;
        return { ...source, rough_notation: roughNotationToSave };
      }),
    };
  });

/** The mark a row draws now: its edited type and style over the one it loaded. */
export const rowRoughNotation = (row: StandoutRow): RoughNotation =>
  withStyle(withNotation(row.roughNotation, row.notation), row.style);

/** The marker a row draws now: its edited type and style over the mark it loaded. */
export const rowMarker = (row: StandoutRow): StandoutMarker =>
  standoutMarkerFrom(row.id, rowRoughNotation(row));

export const isSameWire = (a: Standout[], b: Standout[]): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Focus a menu's first option, without scrolling the page to it. */
export const focusFirstOption = (menu: HTMLElement | null) =>
  menu
    ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
    ?.focus({ preventScroll: true });
